import { useMutation, useQuery } from '@apollo/client/react';
import { type Editor } from '@tiptap/react';
import {
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { toast } from 'sonner';

import Header from '../Header';
import { RightSidebarWrapper } from '../RightSidebar/RightSidebarWrapper';
import useOutletContext from '../useOutletContext';

import {
  GET_ALL_CUSTOMER_END_MARKETS,
  GET_COMPANY_OVERVIEW,
  GET_OPERATIONAL_INFRASTRUCTURE,
  type GetAllCustomerEndMarketsResponse,
  type GetCompanyOverviewResponse,
  type GetOperationalInfrastructureResponse,
  UPDATE_COMPANY_OVERVIEW,
  type UpdateCompanyOverviewResponse,
  UPSERT_OPERATIONAL_INFRASTRUCTURE,
  type UpsertOperationalInfrastructureResponse
} from './graphql';
import { MmriTab } from './MmriTab';
import { ProductsTab } from './ProductsTab';
import { ServicesTab } from './ServicesTab';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import { EditButton } from '@/components/EditButton';
import FileInput from '@/components/FileInput';
import NumberField from '@/components/NumberField';
import RichTextField from '@/components/RichTextField';
import RichTextFieldReadOnly from '@/components/RichTextFieldReadOnly';
import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from '@/components/ui/input-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatCurrency, formatPercent } from '@/lib/utils';

const Card = ({ children }: PropsWithChildren) => (
  <div className="bg-card @container rounded-md border p-5">{children}</div>
);

const SectionHeader = ({
  actions,
  title
}: {
  actions?: ReactNode;
  title: string;
}) => (
  <div className="flex h-9 items-center justify-between gap-3">
    <h2 className="text-lg font-semibold">{title}</h2>
    {actions}
  </div>
);

const TextAreaField = ({
  label,
  value = ''
}: {
  label: string;
  value?: string;
}) => (
  <div className="space-y-2">
    <div className="text-sm font-medium">{label}</div>
    <RichTextFieldReadOnly html={value} />
  </div>
);

const MutedTextareaField = ({
  label,
  value = ''
}: {
  label: string;
  value?: string;
}) => (
  <div className="space-y-2">
    <div className="text-sm font-medium">{label}</div>
    <RichTextFieldReadOnly html={value} />
  </div>
);

const CapexValueRow = ({
  label,
  value = '-'
}: {
  label: string;
  value?: string;
}) => (
  <div className="flex min-w-0 items-center justify-between gap-4 text-sm">
    <span className="shrink-0 text-sm">{label}</span>
    <TruncatedTextTooltip className="text-muted-foreground min-w-0 text-sm font-medium">
      {value}
    </TruncatedTextTooltip>
  </div>
);

const CapexSwitchRow = ({
  checked = false,
  label
}: {
  checked?: boolean;
  label: string;
}) => (
  <div className="flex items-center justify-between gap-4">
    <div className="text-sm font-medium">{label}</div>
    <Switch
      checked={checked}
      disabled
    />
  </div>
);

const CustomerCheckRow = ({
  checked = false,
  label
}: {
  checked?: boolean;
  label: string;
}) => (
  <div className="flex items-center justify-between gap-4">
    <div className="text-sm font-medium">{label}</div>
    <Checkbox
      checked={checked}
      disabled
    />
  </div>
);

// Helper to format numbers for display
const formatValue = (value: null | number | string | undefined): string => {
  if (value == null || value === '') return '-';
  return String(value);
};

const InvestmentsDirectIdCompany = () => {
  const { id, loading: outletLoading, targetEntityId } = useOutletContext();
  const mmriTargetEntityId =
    typeof targetEntityId === 'string' ? targetEntityId : null;

  const {
    data: companyOverviewData,
    loading: companyOverviewLoading,
    refetch: refetchCompanyOverview
  } = useQuery<GetCompanyOverviewResponse, { deal_opportunity_id: string }>(
    GET_COMPANY_OVERVIEW,
    {
      skip: !id,
      variables: { deal_opportunity_id: id }
    }
  );

  const { data: endMarketsData } = useQuery<GetAllCustomerEndMarketsResponse>(
    GET_ALL_CUSTOMER_END_MARKETS
  );

  const { data: infrastructureData, refetch: refetchInfrastructure } = useQuery<
    GetOperationalInfrastructureResponse,
    { dealOpportunityId: string }
  >(GET_OPERATIONAL_INFRASTRUCTURE, {
    skip: !id,
    variables: { dealOpportunityId: id }
  });

  const [updateCompanyOverview, { loading: isUpdatingOverview }] =
    useMutation<UpdateCompanyOverviewResponse>(UPDATE_COMPANY_OVERVIEW);

  const [
    upsertOperationalInfrastructure,
    { loading: isUpsertingInfrastructure }
  ] = useMutation<UpsertOperationalInfrastructureResponse>(
    UPSERT_OPERATIONAL_INFRASTRUCTURE
  );

  const companyOverview = companyOverviewData?.companyOverviewByDeal;
  const allEndMarkets = endMarketsData?.customerEndMarkets ?? [];
  const operationalInfrastructure =
    infrastructureData?.operationalInfrastructureByDeal;

  const selectedEndMarketNames = useMemo(
    () =>
      new Set((companyOverview?.customerEndMarketIds ?? []).map(em => em.name)),
    [companyOverview?.customerEndMarketIds]
  );

  const [activeTab, setActiveTab] = useState('overview');
  const [isSupplierGeneralOpen, setIsSupplierGeneralOpen] = useState(false);
  const [isSupplierCommentaryOpen, setIsSupplierCommentaryOpen] =
    useState(false);
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);
  const [isCapexOverviewOpen, setIsCapexOverviewOpen] = useState(false);
  const [isCapexBreakdownOpen, setIsCapexBreakdownOpen] = useState(false);
  const [isCapexCommentaryOpen, setIsCapexCommentaryOpen] = useState(false);
  const [isCustomerGeneralOpen, setIsCustomerGeneralOpen] = useState(false);
  const [isCustomerRevenueOpen, setIsCustomerRevenueOpen] = useState(false);
  const [isCustomerConcentrationOpen, setIsCustomerConcentrationOpen] =
    useState(false);
  const [isCustomerCommentaryOpen, setIsCustomerCommentaryOpen] =
    useState(false);
  const [isInfrastructureOpen, setIsInfrastructureOpen] = useState(false);

  const [capexOverviewDraft, setCapexOverviewDraft] = useState({
    assetIntensive: false,
    assetLight: false,
    referencePoint: ''
  });
  const [capexBreakdownDraft, setCapexBreakdownDraft] = useState({
    growthCapex: '',
    growthCapexEbitda: '',
    maintenanceCapex: '',
    maintenanceCapexEbitda: '',
    totalCapex: '',
    totalCapexEbitda: ''
  });
  const [customerGeneralDraft, setCustomerGeneralDraft] = useState({
    averageTenure: '',
    customerTypeSelection: 'core' as 'all' | 'core' | 'top_10',
    endMarketB2B: false,
    endMarketB2C: false,
    endMarketGovernment: false,
    retentionPercentage: ''
  });
  const [customerRevenueDraft, setCustomerRevenueDraft] = useState({
    oneTimeRevenue: '',
    recurringRevenue: '',
    reoccurringRevenue: ''
  });
  const [customerConcentrationDraft, setCustomerConcentrationDraft] = useState({
    top1: '',
    top10: '',
    top3: '',
    top5: ''
  });
  const businessDescriptionFieldRef = useRef<Editor>(null);
  const competitiveEdgeFieldRef = useRef<Editor>(null);
  const capexCommentaryFieldRef = useRef<Editor>(null);
  const customerCommentaryFieldRef = useRef<Editor>(null);
  const contractCommentaryFieldRef = useRef<Editor>(null);
  const supplierConcentrationCommentaryFieldRef = useRef<Editor>(null);
  const supplierKeyCommentaryFieldRef = useRef<Editor>(null);
  const supplierDomesticInternationalFieldRef = useRef<Editor>(null);
  const supplierRedundancyAlternateSourcesFieldRef = useRef<Editor>(null);
  const supplierCommodityExposureFieldRef = useRef<Editor>(null);
  const supplierPricingTrendsFieldRef = useRef<Editor>(null);
  const infrastructureTechnologyStackFieldRef = useRef<Editor>(null);
  const infrastructureSecurityFieldRef = useRef<Editor>(null);
  const infrastructureScalabilityFieldRef = useRef<Editor>(null);
  const infrastructureDevelopmentFieldRef = useRef<Editor>(null);
  const infrastructureSupportFieldRef = useRef<Editor>(null);
  useEffect(() => {
    if (isCapexOverviewOpen && companyOverview) {
      setCapexOverviewDraft({
        assetIntensive: companyOverview.isCapexIntensive ?? false,
        assetLight: companyOverview.isCapexAssetLight ?? false,
        referencePoint: companyOverview.capexReferencePoint ?? ''
      });
    }
  }, [isCapexOverviewOpen, companyOverview]);

  useEffect(() => {
    if (isCapexBreakdownOpen && companyOverview) {
      setCapexBreakdownDraft({
        growthCapex: companyOverview.growthCapexSpend?.toString() ?? '',
        growthCapexEbitda:
          companyOverview.growthCapexPercentage != null
            ? String(companyOverview.growthCapexPercentage * 100)
            : '',
        maintenanceCapex:
          companyOverview.maintenanceCapexSpend?.toString() ?? '',
        maintenanceCapexEbitda:
          companyOverview.maintenanceCapexPercentage != null
            ? String(companyOverview.maintenanceCapexPercentage * 100)
            : '',
        totalCapex: companyOverview.totalCapexSpend?.toString() ?? '',
        totalCapexEbitda:
          companyOverview.capexPercentageOfRevenue != null
            ? String(companyOverview.capexPercentageOfRevenue * 100)
            : ''
      });
    }
  }, [isCapexBreakdownOpen, companyOverview]);

  useEffect(() => {
    if (isCustomerGeneralOpen && companyOverview) {
      const raw = companyOverview.customerTypeSelection ?? 'core';
      setCustomerGeneralDraft({
        averageTenure: companyOverview.customerAverageTenure?.toString() ?? '',
        customerTypeSelection: raw === 'all' || raw === 'top_10' ? raw : 'core',
        endMarketB2B: selectedEndMarketNames.has('b2b'),
        endMarketB2C: selectedEndMarketNames.has('b2c'),
        endMarketGovernment: selectedEndMarketNames.has('government'),
        retentionPercentage:
          companyOverview.customerRetentionPercentage != null
            ? String(companyOverview.customerRetentionPercentage * 100)
            : ''
      });
    }
  }, [isCustomerGeneralOpen, companyOverview, selectedEndMarketNames]);

  useEffect(() => {
    if (isCustomerRevenueOpen && companyOverview) {
      setCustomerRevenueDraft({
        oneTimeRevenue:
          companyOverview.projectBasedRevenuePercentage != null
            ? String(companyOverview.projectBasedRevenuePercentage * 100)
            : '',
        recurringRevenue:
          companyOverview.recurringRevenuePercentage != null
            ? String(companyOverview.recurringRevenuePercentage * 100)
            : '',
        reoccurringRevenue:
          companyOverview.reOccurringRevenuePercentage != null
            ? String(companyOverview.reOccurringRevenuePercentage * 100)
            : ''
      });
    }
  }, [isCustomerRevenueOpen, companyOverview]);

  useEffect(() => {
    if (isCustomerConcentrationOpen && companyOverview) {
      setCustomerConcentrationDraft({
        top1:
          companyOverview.concentrationTop1 != null
            ? String(companyOverview.concentrationTop1 * 100)
            : '',
        top10:
          companyOverview.concentrationTop10 != null
            ? String(companyOverview.concentrationTop10 * 100)
            : '',
        top3:
          companyOverview.concentrationTop3 != null
            ? String(companyOverview.concentrationTop3 * 100)
            : '',
        top5:
          companyOverview.concentrationTop5 != null
            ? String(companyOverview.concentrationTop5 * 100)
            : ''
      });
    }
  }, [isCustomerConcentrationOpen, companyOverview]);

  const handleUpdateOverview = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (!companyOverview) return;
    if (
      !businessDescriptionFieldRef.current ||
      !competitiveEdgeFieldRef.current
    ) {
      toast.error('Unable to read overview fields');
      return;
    }

    const formData = new FormData(event.currentTarget);
    const qsbsReviewFile = formData.get('company-qsbs-field');

    if (qsbsReviewFile instanceof File && qsbsReviewFile.size > 0) {
      qsbsReview = await fileToBase64(qsbsReviewFile);
    }

    try {
      const { data } = await updateCompanyOverview({
        variables: {
          id: companyOverview.id,
          values: {
            businessDescription: businessDescriptionFieldRef.current.isEmpty
              ? null
              : businessDescriptionFieldRef.current.getHTML(),
            competitiveEdge: competitiveEdgeFieldRef.current.isEmpty
              ? null
              : competitiveEdgeFieldRef.current.getHTML()
          }
        }
      });
      if (data?.updateCompanyOverview.ok) {
        toast.success('Overview updated successfully');
        setIsOverviewOpen(false);
        void refetchCompanyOverview();
      } else {
        toast.error(
          data?.updateCompanyOverview.errors[0] ?? 'Failed to update overview'
        );
      }
    } catch {
      toast.error('Failed to update overview');
    }
  };

  const handleUpdateSupplierGeneral = async () => {
    if (!companyOverview) return;
    if (
      !supplierDomesticInternationalFieldRef.current ||
      !supplierRedundancyAlternateSourcesFieldRef.current ||
      !supplierCommodityExposureFieldRef.current ||
      !supplierPricingTrendsFieldRef.current
    ) {
      toast.error('Unable to read supplier general');
      return;
    }
    try {
      const { data } = await updateCompanyOverview({
        variables: {
          id: companyOverview.id,
          values: {
            commodityExposure: supplierCommodityExposureFieldRef.current.isEmpty
              ? null
              : supplierCommodityExposureFieldRef.current.getHTML(),
            domesticInternational: supplierDomesticInternationalFieldRef.current
              .isEmpty
              ? null
              : supplierDomesticInternationalFieldRef.current.getHTML(),
            pricingTrends: supplierPricingTrendsFieldRef.current.isEmpty
              ? null
              : supplierPricingTrendsFieldRef.current.getHTML(),
            redundancyAlternateSources:
              supplierRedundancyAlternateSourcesFieldRef.current.isEmpty
                ? null
                : supplierRedundancyAlternateSourcesFieldRef.current.getHTML()
          }
        }
      });
      if (data?.updateCompanyOverview.ok) {
        toast.success('Supplier general updated successfully');
        setIsSupplierGeneralOpen(false);
        void refetchCompanyOverview();
      } else {
        toast.error(
          data?.updateCompanyOverview.errors[0] ??
            'Failed to update supplier general'
        );
      }
    } catch {
      toast.error('Failed to update supplier general');
    }
  };

  const handleUpdateSupplierCommentary = async () => {
    if (!companyOverview) return;
    if (
      !supplierConcentrationCommentaryFieldRef.current ||
      !supplierKeyCommentaryFieldRef.current
    ) {
      toast.error('Unable to read supplier commentary');
      return;
    }
    try {
      const { data } = await updateCompanyOverview({
        variables: {
          id: companyOverview.id,
          values: {
            customerCommentary: supplierConcentrationCommentaryFieldRef
              .current.isEmpty
              ? null
              : supplierConcentrationCommentaryFieldRef.current.getHTML(),
            keySupplierCommentary: supplierKeyCommentaryFieldRef.current.isEmpty
              ? null
              : supplierKeyCommentaryFieldRef.current.getHTML()
          }
        }
      });
      if (data?.updateCompanyOverview.ok) {
        toast.success('Supplier commentary updated successfully');
        setIsSupplierCommentaryOpen(false);
        void refetchCompanyOverview();
      } else {
        toast.error(
          data?.updateCompanyOverview.errors[0] ??
            'Failed to update supplier commentary'
        );
      }
    } catch {
      toast.error('Failed to update supplier commentary');
    }
  };

  const handleUpdateCapexOverview = async () => {
    if (!companyOverview) return;
    try {
      const { data } = await updateCompanyOverview({
        variables: {
          id: companyOverview.id,
          values: {
            capexReferencePoint: capexOverviewDraft.referencePoint || null,
            isCapexAssetLight: capexOverviewDraft.assetLight,
            isCapexIntensive: capexOverviewDraft.assetIntensive
          }
        }
      });
      if (data?.updateCompanyOverview.ok) {
        toast.success('CapEx overview updated successfully');
        setIsCapexOverviewOpen(false);
        void refetchCompanyOverview();
      } else {
        toast.error(
          data?.updateCompanyOverview.errors[0] ??
            'Failed to update CapEx overview'
        );
      }
    } catch {
      toast.error('Failed to update CapEx overview');
    }
  };

  const handleUpdateCapexBreakdown = async () => {
    if (!companyOverview) return;
    try {
      const { data } = await updateCompanyOverview({
        variables: {
          id: companyOverview.id,
          values: {
            capexPercentageOfRevenue: capexBreakdownDraft.totalCapexEbitda
              ? parseFloat(capexBreakdownDraft.totalCapexEbitda) / 100
              : null,
            growthCapexPercentage: capexBreakdownDraft.growthCapexEbitda
              ? parseFloat(capexBreakdownDraft.growthCapexEbitda) / 100
              : null,
            growthCapexSpend: capexBreakdownDraft.growthCapex
              ? parseFloat(capexBreakdownDraft.growthCapex)
              : null,
            maintenanceCapexPercentage:
              capexBreakdownDraft.maintenanceCapexEbitda
                ? parseFloat(capexBreakdownDraft.maintenanceCapexEbitda) / 100
                : null,
            maintenanceCapexSpend: capexBreakdownDraft.maintenanceCapex
              ? parseFloat(capexBreakdownDraft.maintenanceCapex)
              : null,
            totalCapexSpend: capexBreakdownDraft.totalCapex
              ? parseFloat(capexBreakdownDraft.totalCapex)
              : null
          }
        }
      });
      if (data?.updateCompanyOverview.ok) {
        toast.success('CapEx breakdown updated successfully');
        setIsCapexBreakdownOpen(false);
        void refetchCompanyOverview();
      } else {
        toast.error(
          data?.updateCompanyOverview.errors[0] ??
            'Failed to update CapEx breakdown'
        );
      }
    } catch {
      toast.error('Failed to update CapEx breakdown');
    }
  };

  const handleUpdateCapexCommentary = async () => {
    if (!companyOverview) return;
    if (!capexCommentaryFieldRef.current) {
      toast.error('Unable to read CapEx commentary');
      return;
    }
    try {
      const { data } = await updateCompanyOverview({
        variables: {
          id: companyOverview.id,
          values: {
            capexCommentary: capexCommentaryFieldRef.current.isEmpty
              ? null
              : capexCommentaryFieldRef.current.getHTML()
          }
        }
      });
      if (data?.updateCompanyOverview.ok) {
        toast.success('CapEx commentary updated successfully');
        setIsCapexCommentaryOpen(false);
        void refetchCompanyOverview();
      } else {
        toast.error(
          data?.updateCompanyOverview.errors[0] ??
            'Failed to update CapEx commentary'
        );
      }
    } catch {
      toast.error('Failed to update CapEx commentary');
    }
  };

  const handleUpdateCustomerGeneral = async () => {
    if (!companyOverview) return;
    try {
      const selectedEndMarketIds: string[] = [];
      for (const em of allEndMarkets) {
        if (em.name === 'b2b' && customerGeneralDraft.endMarketB2B) {
          selectedEndMarketIds.push(em.id);
        }
        if (em.name === 'b2c' && customerGeneralDraft.endMarketB2C) {
          selectedEndMarketIds.push(em.id);
        }
        if (
          em.name === 'government' &&
          customerGeneralDraft.endMarketGovernment
        ) {
          selectedEndMarketIds.push(em.id);
        }
      }

      const { data } = await updateCompanyOverview({
        variables: {
          id: companyOverview.id,
          values: {
            customerAverageTenure: customerGeneralDraft.averageTenure
              ? parseInt(customerGeneralDraft.averageTenure, 10)
              : null,
            customerEndMarketIds: selectedEndMarketIds,
            customerRetentionPercentage:
              customerGeneralDraft.retentionPercentage
                ? parseFloat(customerGeneralDraft.retentionPercentage) / 100
                : null,
            customerTypeSelection: customerGeneralDraft.customerTypeSelection
          }
        }
      });
      if (data?.updateCompanyOverview.ok) {
        toast.success('Customer general updated successfully');
        setIsCustomerGeneralOpen(false);
        void refetchCompanyOverview();
      } else {
        toast.error(
          data?.updateCompanyOverview.errors[0] ??
            'Failed to update customer general'
        );
      }
    } catch {
      toast.error('Failed to update customer general');
    }
  };

  const handleUpdateCustomerRevenue = async () => {
    if (!companyOverview) return;
    try {
      const { data } = await updateCompanyOverview({
        variables: {
          id: companyOverview.id,
          values: {
            projectBasedRevenuePercentage: customerRevenueDraft.oneTimeRevenue
              ? parseFloat(customerRevenueDraft.oneTimeRevenue) / 100
              : null,
            recurringRevenuePercentage: customerRevenueDraft.recurringRevenue
              ? parseFloat(customerRevenueDraft.recurringRevenue) / 100
              : null,
            reOccurringRevenuePercentage:
              customerRevenueDraft.reoccurringRevenue
                ? parseFloat(customerRevenueDraft.reoccurringRevenue) / 100
                : null
          }
        }
      });
      if (data?.updateCompanyOverview.ok) {
        toast.success('Customer revenue updated successfully');
        setIsCustomerRevenueOpen(false);
        void refetchCompanyOverview();
      } else {
        toast.error(
          data?.updateCompanyOverview.errors[0] ??
            'Failed to update customer revenue'
        );
      }
    } catch {
      toast.error('Failed to update customer revenue');
    }
  };

  const handleUpdateCustomerConcentration = async () => {
    if (!companyOverview) return;
    try {
      const { data } = await updateCompanyOverview({
        variables: {
          id: companyOverview.id,
          values: {
            concentrationTop1: customerConcentrationDraft.top1
              ? parseFloat(customerConcentrationDraft.top1) / 100
              : null,
            concentrationTop10: customerConcentrationDraft.top10
              ? parseFloat(customerConcentrationDraft.top10) / 100
              : null,
            concentrationTop3: customerConcentrationDraft.top3
              ? parseFloat(customerConcentrationDraft.top3) / 100
              : null,
            concentrationTop5: customerConcentrationDraft.top5
              ? parseFloat(customerConcentrationDraft.top5) / 100
              : null
          }
        }
      });
      if (data?.updateCompanyOverview.ok) {
        toast.success('Customer concentration updated successfully');
        setIsCustomerConcentrationOpen(false);
        void refetchCompanyOverview();
      } else {
        toast.error(
          data?.updateCompanyOverview.errors[0] ??
            'Failed to update customer concentration'
        );
      }
    } catch {
      toast.error('Failed to update customer concentration');
    }
  };

  const handleUpdateCustomerCommentary = async () => {
    if (!companyOverview) return;
    if (
      !customerCommentaryFieldRef.current ||
      !contractCommentaryFieldRef.current
    ) {
      toast.error('Unable to read customer commentary');
      return;
    }
    try {
      const { data } = await updateCompanyOverview({
        variables: {
          id: companyOverview.id,
          values: {
            contractCommentary: contractCommentaryFieldRef.current.isEmpty
              ? null
              : contractCommentaryFieldRef.current.getHTML(),
            customerCommentary: customerCommentaryFieldRef.current.isEmpty
              ? null
              : customerCommentaryFieldRef.current.getHTML()
          }
        }
      });
      if (data?.updateCompanyOverview.ok) {
        toast.success('Customer commentary updated successfully');
        setIsCustomerCommentaryOpen(false);
        void refetchCompanyOverview();
      } else {
        toast.error(
          data?.updateCompanyOverview.errors[0] ??
            'Failed to update customer commentary'
        );
      }
    } catch {
      toast.error('Failed to update customer commentary');
    }
  };

  const handleSaveInfrastructure = async () => {
    if (
      !infrastructureDevelopmentFieldRef.current ||
      !infrastructureScalabilityFieldRef.current ||
      !infrastructureSecurityFieldRef.current ||
      !infrastructureSupportFieldRef.current ||
      !infrastructureTechnologyStackFieldRef.current
    ) {
      toast.error('Unable to read infrastructure fields');
      return;
    }

    try {
      const baseValues = {
        development: infrastructureDevelopmentFieldRef.current.isEmpty
          ? null
          : infrastructureDevelopmentFieldRef.current.getHTML(),
        scalability: infrastructureScalabilityFieldRef.current.isEmpty
          ? null
          : infrastructureScalabilityFieldRef.current.getHTML(),
        security: infrastructureSecurityFieldRef.current.isEmpty
          ? null
          : infrastructureSecurityFieldRef.current.getHTML(),
        support: infrastructureSupportFieldRef.current.isEmpty
          ? null
          : infrastructureSupportFieldRef.current.getHTML(),
        technologyStack: infrastructureTechnologyStackFieldRef.current.isEmpty
          ? null
          : infrastructureTechnologyStackFieldRef.current.getHTML()
      };

      const { data } = await upsertOperationalInfrastructure({
        variables: {
          dealOpportunityId: id,
          values: baseValues
        }
      });
      if (data?.upsertOperationalInfrastructure.ok) {
        toast.success('Infrastructure saved successfully');
        setIsInfrastructureOpen(false);
        void refetchInfrastructure();
      } else {
        toast.error(
          data?.upsertOperationalInfrastructure.message ??
            'Failed to save infrastructure'
        );
      }
    } catch {
      toast.error('Failed to save infrastructure');
    }
  };

  if ((outletLoading || companyOverviewLoading) && !companyOverview) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  return (
    <>
      <Header />
      <RightSidebarWrapper>
        <div className="min-w-0 space-y-6 px-5 pt-5 pb-16.5 md:pl-14">
          <Tabs
            className="gap-5"
            onValueChange={setActiveTab}
            value={activeTab}
          >
            <TabsList className="h-auto! w-full justify-start overflow-x-auto overflow-y-hidden">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="products">Products & Services</TabsTrigger>
              <TabsTrigger value="capex">CapEx</TabsTrigger>
              <TabsTrigger value="customer">Customer</TabsTrigger>
              <TabsTrigger value="supplier">Supplier</TabsTrigger>
              <TabsTrigger value="infra">
                Infrastructure & Technology
              </TabsTrigger>
              <TabsTrigger value="mmri">MMRI</TabsTrigger>
            </TabsList>
            <TabsContent
              className="space-y-2.5"
              value="overview"
            >
              <SectionHeader
                actions={
                  <Dialog
                    onOpenChange={setIsOverviewOpen}
                    open={isOverviewOpen}
                  >
                    <DialogTrigger asChild>
                      <EditButton />
                    </DialogTrigger>
                    <DialogContent aria-describedby={undefined}>
                      <DialogHeader>
                        <DialogTitle>
                          Company Overview
                          <Badge variant="yellow">Unsaved</Badge>
                        </DialogTitle>
                      </DialogHeader>
                      <DialogBody>
                        <form
                          id="company-overview-form"
                          noValidate
                          onSubmit={event => void handleUpdateOverview(event)}
                        >
                          <FieldGroup>
                            <Field>
                              <FieldLabel htmlFor="company-business-field">
                                Business Description
                              </FieldLabel>
                              <RichTextField
                                defaultValue={
                                  companyOverview?.businessDescription ?? ''
                                }
                                id="company-business-field"
                                ref={businessDescriptionFieldRef}
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="company-competitive-field">
                                Competitive Edge
                              </FieldLabel>
                              <RichTextField
                                defaultValue={
                                  companyOverview?.competitiveEdge ?? ''
                                }
                                id="company-competitive-field"
                                ref={competitiveEdgeFieldRef}
                              />
                            </Field>
                          </FieldGroup>
                        </form>
                      </DialogBody>
                      <DialogFooter>
                        <Button
                          disabled={isUpdatingOverview}
                          form="company-overview-form"
                          type="submit"
                        >
                          {isUpdatingOverview && <Spinner />}
                          Save
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                }
                title="Overview"
              />
              <Card>
                <div className="space-y-5">
                  <div className="border-b pb-2.5">
                    <h3 className="text-base font-medium">Overview</h3>
                  </div>
                  <TextAreaField
                    label="Business Description"
                    value={companyOverview?.businessDescription ?? ''}
                  />
                  <TextAreaField
                    label="Competitive Edge"
                    value={companyOverview?.competitiveEdge ?? ''}
                  />
                </div>
              </Card>
            </TabsContent>
            <TabsContent
              className="space-y-2.5"
              value="products"
            >
              <ProductsTab dealOpportunityId={id} />
              <ServicesTab dealOpportunityId={id} />
            </TabsContent>
            <TabsContent
              className="space-y-2.5"
              value="capex"
            >
              <div className="space-y-2.5">
                <SectionHeader
                  actions={
                    <Dialog
                      onOpenChange={setIsCapexOverviewOpen}
                      open={isCapexOverviewOpen}
                    >
                      <DialogTrigger asChild>
                        <EditButton />
                      </DialogTrigger>
                      <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>
                            CapEx Overview
                            <Badge variant="yellow">Unsaved</Badge>
                          </DialogTitle>
                        </DialogHeader>

                        <DialogBody>
                          <FieldGroup>
                            <Field>
                              <FieldLabel htmlFor="capex-reference-field">
                                Reference Point
                              </FieldLabel>
                              <Input
                                id="capex-reference-field"
                                onChange={event => {
                                  setCapexOverviewDraft(current => ({
                                    ...current,
                                    referencePoint: event.target.value
                                  }));
                                }}
                                placeholder="Enter Value"
                                value={capexOverviewDraft.referencePoint}
                              />
                            </Field>
                            <Field>
                              <FieldLabel>
                                Capital Investment Requirements
                              </FieldLabel>
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={capexOverviewDraft.assetLight}
                                    onCheckedChange={checked => {
                                      setCapexOverviewDraft(current => ({
                                        ...current,
                                        assetLight: checked
                                      }));
                                    }}
                                  />
                                  <span className="text-sm">Asset-Light</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={capexOverviewDraft.assetIntensive}
                                    onCheckedChange={checked => {
                                      setCapexOverviewDraft(current => ({
                                        ...current,
                                        assetIntensive: checked
                                      }));
                                    }}
                                  />
                                  <span className="text-sm">
                                    Asset-Intensive
                                  </span>
                                </div>
                              </div>
                            </Field>
                          </FieldGroup>
                        </DialogBody>

                        <DialogFooter>
                          <Button
                            disabled={isUpdatingOverview}
                            onClick={() => void handleUpdateCapexOverview()}
                            type="button"
                          >
                            {isUpdatingOverview && <Spinner />}
                            Save
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  }
                  title="Overview"
                />
                <Card>
                  <div className="space-y-5">
                    <CapexValueRow
                      label="Reference Point"
                      value={formatValue(companyOverview?.capexReferencePoint)}
                    />
                    <div className="border-b pb-2.5">
                      <h3 className="text-base font-medium">
                        Capital Investment Requirements
                      </h3>
                    </div>
                    <div className="grid gap-5 @md:grid-cols-2">
                      <CapexSwitchRow
                        checked={companyOverview?.isCapexAssetLight ?? false}
                        label="Asset-Light"
                      />
                      <CapexSwitchRow
                        checked={companyOverview?.isCapexIntensive ?? false}
                        label="Asset-Intensive"
                      />
                    </div>
                  </div>
                </Card>
              </div>
              <div className="space-y-2.5">
                <SectionHeader
                  actions={
                    <Dialog
                      onOpenChange={setIsCapexBreakdownOpen}
                      open={isCapexBreakdownOpen}
                    >
                      <DialogTrigger asChild>
                        <EditButton />
                      </DialogTrigger>
                      <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>
                            CapEx Breakdown
                            <Badge variant="yellow">Unsaved</Badge>
                          </DialogTitle>
                        </DialogHeader>

                        <DialogBody>
                          <FieldGroup>
                            <div className="grid grid-cols-2 gap-x-2">
                              <Field>
                                <FieldLabel htmlFor="capex-total-field">
                                  Total CapEx
                                </FieldLabel>
                                <InputGroup>
                                  <InputGroupAddon>
                                    <InputGroupText>$</InputGroupText>
                                  </InputGroupAddon>
                                  <NumberField
                                    id="capex-total-field"
                                    onChange={event => {
                                      setCapexBreakdownDraft(current => ({
                                        ...current,
                                        totalCapex: event.target.value.replace(
                                          /,/g,
                                          ''
                                        )
                                      }));
                                    }}
                                    placeholder="Enter Value"
                                    value={capexBreakdownDraft.totalCapex}
                                  />
                                </InputGroup>
                              </Field>
                              <Field>
                                <FieldLabel htmlFor="capex-total-ebitda-field">
                                  Total CapEx & of Ref. EBITDA
                                </FieldLabel>
                                <InputGroup>
                                  <NumberField
                                    id="capex-total-ebitda-field"
                                    max={100}
                                    onChange={event => {
                                      setCapexBreakdownDraft(current => ({
                                        ...current,
                                        totalCapexEbitda:
                                          event.target.value.replace(/,/g, '')
                                      }));
                                    }}
                                    placeholder="Enter Value"
                                    value={capexBreakdownDraft.totalCapexEbitda}
                                  />
                                  <InputGroupAddon align="inline-end">
                                    <InputGroupText>%</InputGroupText>
                                  </InputGroupAddon>
                                </InputGroup>
                              </Field>
                            </div>
                            <div className="grid grid-cols-2 gap-x-2">
                              <Field>
                                <FieldLabel htmlFor="capex-maintenance-field">
                                  Maintenance CapEx
                                </FieldLabel>
                                <InputGroup>
                                  <InputGroupAddon>
                                    <InputGroupText>$</InputGroupText>
                                  </InputGroupAddon>
                                  <NumberField
                                    id="capex-maintenance-field"
                                    onChange={event => {
                                      setCapexBreakdownDraft(current => ({
                                        ...current,
                                        maintenanceCapex:
                                          event.target.value.replace(/,/g, '')
                                      }));
                                    }}
                                    placeholder="Enter Value"
                                    value={capexBreakdownDraft.maintenanceCapex}
                                  />
                                </InputGroup>
                              </Field>
                              <Field>
                                <FieldLabel htmlFor="capex-maintenance-ebitda-field">
                                  Maintenance CapEx & of Ref. EBITDA
                                </FieldLabel>
                                <InputGroup>
                                  <NumberField
                                    id="capex-maintenance-ebitda-field"
                                    max={100}
                                    onChange={event => {
                                      setCapexBreakdownDraft(current => ({
                                        ...current,
                                        maintenanceCapexEbitda:
                                          event.target.value.replace(/,/g, '')
                                      }));
                                    }}
                                    placeholder="Enter Value"
                                    value={
                                      capexBreakdownDraft.maintenanceCapexEbitda
                                    }
                                  />
                                  <InputGroupAddon align="inline-end">
                                    <InputGroupText>%</InputGroupText>
                                  </InputGroupAddon>
                                </InputGroup>
                              </Field>
                            </div>
                            <div className="grid grid-cols-2 gap-x-2">
                              <Field>
                                <FieldLabel htmlFor="capex-growth-field">
                                  Growth CapEx
                                </FieldLabel>
                                <InputGroup>
                                  <InputGroupAddon>
                                    <InputGroupText>$</InputGroupText>
                                  </InputGroupAddon>
                                  <NumberField
                                    id="capex-growth-field"
                                    onChange={event => {
                                      setCapexBreakdownDraft(current => ({
                                        ...current,
                                        growthCapex: event.target.value.replace(
                                          /,/g,
                                          ''
                                        )
                                      }));
                                    }}
                                    placeholder="Enter Value"
                                    value={capexBreakdownDraft.growthCapex}
                                  />
                                </InputGroup>
                              </Field>
                              <Field>
                                <FieldLabel htmlFor="capex-growth-ebitda-field">
                                  Growth CapEx & of Ref. EBITDA
                                </FieldLabel>
                                <InputGroup>
                                  <NumberField
                                    id="capex-growth-ebitda-field"
                                    max={100}
                                    onChange={event => {
                                      setCapexBreakdownDraft(current => ({
                                        ...current,
                                        growthCapexEbitda:
                                          event.target.value.replace(/,/g, '')
                                      }));
                                    }}
                                    placeholder="Enter Value"
                                    value={
                                      capexBreakdownDraft.growthCapexEbitda
                                    }
                                  />
                                  <InputGroupAddon align="inline-end">
                                    <InputGroupText>%</InputGroupText>
                                  </InputGroupAddon>
                                </InputGroup>
                              </Field>
                            </div>
                          </FieldGroup>
                        </DialogBody>

                        <DialogFooter>
                          <Button
                            disabled={isUpdatingOverview}
                            onClick={() => void handleUpdateCapexBreakdown()}
                            type="button"
                          >
                            {isUpdatingOverview && <Spinner />}
                            Save
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  }
                  title="Breakdown"
                />
                <Card>
                  <div className="grid gap-5 @md:grid-cols-2">
                    <div className="space-y-3">
                      <CapexValueRow
                        label="Total CapEx"
                        value={formatCurrency(companyOverview?.totalCapexSpend)}
                      />
                      <CapexValueRow
                        label="Maintenance CapEx"
                        value={formatCurrency(
                          companyOverview?.maintenanceCapexSpend
                        )}
                      />
                      <CapexValueRow
                        label="Growth CapEx"
                        value={formatCurrency(
                          companyOverview?.growthCapexSpend
                        )}
                      />
                    </div>
                    <div className="space-y-3">
                      <CapexValueRow
                        label="Total CapEx & of Ref. EBITDA"
                        value={formatPercent(
                          companyOverview?.capexPercentageOfRevenue,
                          { isDecimal: true, showNullAs: '-' }
                        )}
                      />
                      <CapexValueRow
                        label="Maintenance CapEx & of Ref. EBITDA"
                        value={formatPercent(
                          companyOverview?.maintenanceCapexPercentage,
                          { isDecimal: true, showNullAs: '-' }
                        )}
                      />
                      <CapexValueRow
                        label="Growth CapEx & of Ref. EBITDA"
                        value={formatPercent(
                          companyOverview?.growthCapexPercentage,
                          { isDecimal: true, showNullAs: '-' }
                        )}
                      />
                    </div>
                  </div>
                </Card>
              </div>
              <div className="space-y-2.5">
                <SectionHeader
                  actions={
                    <Dialog
                      onOpenChange={setIsCapexCommentaryOpen}
                      open={isCapexCommentaryOpen}
                    >
                      <DialogTrigger asChild>
                        <EditButton />
                      </DialogTrigger>
                      <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>
                            CapEx Commentary
                            <Badge variant="yellow">Unsaved</Badge>
                          </DialogTitle>
                        </DialogHeader>

                        <DialogBody>
                          <FieldGroup>
                            <Field>
                              <FieldLabel htmlFor="capex-commentary-field">
                                Commentary
                              </FieldLabel>
                              <RichTextField
                                defaultValue={
                                  companyOverview?.capexCommentary ?? ''
                                }
                                id="capex-commentary-field"
                                ref={capexCommentaryFieldRef}
                              />
                            </Field>
                          </FieldGroup>
                        </DialogBody>

                        <DialogFooter>
                          <Button
                            disabled={isUpdatingOverview}
                            onClick={() => void handleUpdateCapexCommentary()}
                            type="button"
                          >
                            {isUpdatingOverview && <Spinner />}
                            Save
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  }
                  title="Commentary"
                />
                <Card>
                  <MutedTextareaField
                    label="Commentary"
                    value={companyOverview?.capexCommentary ?? ''}
                  />
                </Card>
              </div>
            </TabsContent>
            <TabsContent
              className="space-y-2.5"
              value="customer"
            >
              <div className="space-y-2.5">
                <SectionHeader
                  actions={
                    <Dialog
                      onOpenChange={setIsCustomerGeneralOpen}
                      open={isCustomerGeneralOpen}
                    >
                      <DialogTrigger asChild>
                        <EditButton />
                      </DialogTrigger>
                      <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>
                            Customer General
                            <Badge variant="yellow">Unsaved</Badge>
                          </DialogTitle>
                        </DialogHeader>

                        <DialogBody>
                          <FieldGroup>
                            <div className="grid grid-cols-2 gap-x-2 @md:grid-cols-3">
                              <Field>
                                <FieldLabel htmlFor="customer-tenure-field">
                                  Average Customer Tenure (Years)
                                </FieldLabel>
                                <Input
                                  id="customer-tenure-field"
                                  onChange={event => {
                                    setCustomerGeneralDraft(current => ({
                                      ...current,
                                      averageTenure: event.target.value
                                    }));
                                  }}
                                  placeholder="Enter Value"
                                  value={customerGeneralDraft.averageTenure}
                                />
                              </Field>
                              <Field>
                                <FieldLabel htmlFor="customer-retention-field">
                                  Customer Retention Percentage
                                </FieldLabel>
                                <InputGroup>
                                  <NumberField
                                    id="customer-retention-field"
                                    onChange={event => {
                                      setCustomerGeneralDraft(current => ({
                                        ...current,
                                        retentionPercentage:
                                          event.target.value.replace(/,/g, '')
                                      }));
                                    }}
                                    placeholder="Enter Value"
                                    value={
                                      customerGeneralDraft.retentionPercentage
                                    }
                                  />
                                  <InputGroupAddon align="inline-end">
                                    <InputGroupText>%</InputGroupText>
                                  </InputGroupAddon>
                                </InputGroup>
                              </Field>
                              <Field>
                                <FieldLabel>Customer Type</FieldLabel>
                                <Select
                                  onValueChange={(
                                    value: 'all' | 'core' | 'top_10'
                                  ) => {
                                    setCustomerGeneralDraft(current => ({
                                      ...current,
                                      customerTypeSelection: value
                                    }));
                                  }}
                                  value={
                                    customerGeneralDraft.customerTypeSelection
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select customer type..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="all">All</SelectItem>
                                    <SelectItem value="core">Core</SelectItem>
                                    <SelectItem value="top_10">
                                      Top 10
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </Field>
                            </div>
                            <Field>
                              <FieldLabel>Customer End Market</FieldLabel>
                              <div className="grid gap-3 @md:grid-cols-2">
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={customerGeneralDraft.endMarketB2C}
                                    onCheckedChange={checked => {
                                      setCustomerGeneralDraft(current => ({
                                        ...current,
                                        endMarketB2C: Boolean(checked)
                                      }));
                                    }}
                                  />
                                  <span className="text-sm">B2C</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={
                                      customerGeneralDraft.endMarketGovernment
                                    }
                                    onCheckedChange={checked => {
                                      setCustomerGeneralDraft(current => ({
                                        ...current,
                                        endMarketGovernment: Boolean(checked)
                                      }));
                                    }}
                                  />
                                  <span className="text-sm">Government</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={customerGeneralDraft.endMarketB2B}
                                    onCheckedChange={checked => {
                                      setCustomerGeneralDraft(current => ({
                                        ...current,
                                        endMarketB2B: Boolean(checked)
                                      }));
                                    }}
                                  />
                                  <span className="text-sm">B2B</span>
                                </div>
                              </div>
                            </Field>
                          </FieldGroup>
                        </DialogBody>

                        <DialogFooter>
                          <Button
                            disabled={isUpdatingOverview}
                            onClick={() => void handleUpdateCustomerGeneral()}
                            type="button"
                          >
                            {isUpdatingOverview && <Spinner />}
                            Save
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  }
                  title="General"
                />
                <Card>
                  <div className="space-y-5">
                    <div className="grid gap-5 @md:grid-cols-2">
                      <CapexValueRow
                        label="Average Customer Tenure (Years)"
                        value={formatValue(
                          companyOverview?.customerAverageTenure
                        )}
                      />
                      <CapexValueRow
                        label="Customer Retention Percentage"
                        value={formatPercent(
                          companyOverview?.customerRetentionPercentage,
                          { isDecimal: true, showNullAs: '-' }
                        )}
                      />
                      <CapexValueRow
                        label="Customer Type"
                        value={
                          companyOverview?.customerTypeSelection === 'all'
                            ? 'All'
                            : companyOverview?.customerTypeSelection ===
                                'top_10'
                              ? 'Top 10'
                              : companyOverview?.customerTypeSelection ===
                                  'core'
                                ? 'Core'
                                : '-'
                        }
                      />
                    </div>
                    <div className="border-b pb-2.5">
                      <h3 className="text-base font-medium">
                        Customer End Market
                      </h3>
                    </div>
                    <div className="grid gap-5 @md:grid-cols-2">
                      <CustomerCheckRow
                        checked={selectedEndMarketNames.has('b2c')}
                        label="B2C"
                      />
                      <CustomerCheckRow
                        checked={selectedEndMarketNames.has('government')}
                        label="Government"
                      />
                      <CustomerCheckRow
                        checked={selectedEndMarketNames.has('b2b')}
                        label="B2B"
                      />
                      <div />
                    </div>
                  </div>
                </Card>
              </div>
              <div className="space-y-2.5">
                <SectionHeader
                  actions={
                    <Dialog
                      onOpenChange={setIsCustomerRevenueOpen}
                      open={isCustomerRevenueOpen}
                    >
                      <DialogTrigger asChild>
                        <EditButton />
                      </DialogTrigger>
                      <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>
                            Customer Revenue
                            <Badge variant="yellow">Unsaved</Badge>
                          </DialogTitle>
                        </DialogHeader>

                        <DialogBody>
                          <FieldGroup>
                            <div className="grid grid-cols-2 gap-x-2">
                              <Field>
                                <FieldLabel htmlFor="customer-recurring-field">
                                  Recurring Revenue
                                </FieldLabel>
                                <InputGroup>
                                  <NumberField
                                    id="customer-recurring-field"
                                    max={100}
                                    onChange={event => {
                                      setCustomerRevenueDraft(current => ({
                                        ...current,
                                        recurringRevenue:
                                          event.target.value.replace(/,/g, '')
                                      }));
                                    }}
                                    placeholder="Enter Value"
                                    value={
                                      customerRevenueDraft.recurringRevenue
                                    }
                                  />
                                  <InputGroupAddon align="inline-end">
                                    <InputGroupText>%</InputGroupText>
                                  </InputGroupAddon>
                                </InputGroup>
                              </Field>
                              <Field>
                                <FieldLabel htmlFor="customer-reoccurring-field">
                                  Reoccurring Revenue
                                </FieldLabel>
                                <InputGroup>
                                  <NumberField
                                    id="customer-reoccurring-field"
                                    max={100}
                                    onChange={event => {
                                      setCustomerRevenueDraft(current => ({
                                        ...current,
                                        reoccurringRevenue:
                                          event.target.value.replace(/,/g, '')
                                      }));
                                    }}
                                    placeholder="Enter Value"
                                    value={
                                      customerRevenueDraft.reoccurringRevenue
                                    }
                                  />
                                  <InputGroupAddon align="inline-end">
                                    <InputGroupText>%</InputGroupText>
                                  </InputGroupAddon>
                                </InputGroup>
                              </Field>
                            </div>
                            <Field>
                              <FieldLabel htmlFor="customer-one-time-field">
                                One-Time Revenue
                              </FieldLabel>
                              <InputGroup>
                                <NumberField
                                  id="customer-one-time-field"
                                  max={100}
                                  onChange={event => {
                                    setCustomerRevenueDraft(current => ({
                                      ...current,
                                      oneTimeRevenue:
                                        event.target.value.replace(/,/g, '')
                                    }));
                                  }}
                                  placeholder="Enter Value"
                                  value={customerRevenueDraft.oneTimeRevenue}
                                />
                                <InputGroupAddon align="inline-end">
                                  <InputGroupText>%</InputGroupText>
                                </InputGroupAddon>
                              </InputGroup>
                            </Field>
                          </FieldGroup>
                        </DialogBody>

                        <DialogFooter>
                          <Button
                            disabled={isUpdatingOverview}
                            onClick={() => void handleUpdateCustomerRevenue()}
                            type="button"
                          >
                            {isUpdatingOverview && <Spinner />}
                            Save
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  }
                  title="Revenue"
                />
                <Card>
                  <div className="grid gap-5 @md:grid-cols-2">
                    <CapexValueRow
                      label="Recurring Revenue"
                      value={formatPercent(
                        companyOverview?.recurringRevenuePercentage,
                        { isDecimal: true, showNullAs: '-' }
                      )}
                    />
                    <CapexValueRow
                      label="Reoccurring Revenue"
                      value={formatPercent(
                        companyOverview?.reOccurringRevenuePercentage,
                        { isDecimal: true, showNullAs: '-' }
                      )}
                    />
                    <CapexValueRow
                      label="One-Time Revenue"
                      value={formatPercent(
                        companyOverview?.projectBasedRevenuePercentage,
                        { isDecimal: true, showNullAs: '-' }
                      )}
                    />
                    <div />
                  </div>
                </Card>
              </div>
              <div className="space-y-2.5">
                <SectionHeader
                  actions={
                    <Dialog
                      onOpenChange={setIsCustomerConcentrationOpen}
                      open={isCustomerConcentrationOpen}
                    >
                      <DialogTrigger asChild>
                        <EditButton />
                      </DialogTrigger>
                      <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>
                            Customer Concentration
                            <Badge variant="yellow">Unsaved</Badge>
                          </DialogTitle>
                        </DialogHeader>

                        <DialogBody>
                          <FieldGroup>
                            <div className="grid grid-cols-2 gap-x-2">
                              <Field>
                                <FieldLabel htmlFor="customer-top1-field">
                                  Top 1
                                </FieldLabel>
                                <InputGroup>
                                  <NumberField
                                    id="customer-top1-field"
                                    max={100}
                                    onChange={event => {
                                      setCustomerConcentrationDraft(
                                        current => ({
                                          ...current,
                                          top1: event.target.value.replace(
                                            /,/g,
                                            ''
                                          )
                                        })
                                      );
                                    }}
                                    placeholder="Enter Value"
                                    value={customerConcentrationDraft.top1}
                                  />
                                  <InputGroupAddon align="inline-end">
                                    <InputGroupText>%</InputGroupText>
                                  </InputGroupAddon>
                                </InputGroup>
                              </Field>
                              <Field>
                                <FieldLabel htmlFor="customer-top3-field">
                                  Top 3
                                </FieldLabel>
                                <InputGroup>
                                  <NumberField
                                    id="customer-top3-field"
                                    max={100}
                                    onChange={event => {
                                      setCustomerConcentrationDraft(
                                        current => ({
                                          ...current,
                                          top3: event.target.value.replace(
                                            /,/g,
                                            ''
                                          )
                                        })
                                      );
                                    }}
                                    placeholder="Enter Value"
                                    value={customerConcentrationDraft.top3}
                                  />
                                  <InputGroupAddon align="inline-end">
                                    <InputGroupText>%</InputGroupText>
                                  </InputGroupAddon>
                                </InputGroup>
                              </Field>
                            </div>
                            <div className="grid grid-cols-2 gap-x-2">
                              <Field>
                                <FieldLabel htmlFor="customer-top5-field">
                                  Top 5
                                </FieldLabel>
                                <InputGroup>
                                  <NumberField
                                    id="customer-top5-field"
                                    max={100}
                                    onChange={event => {
                                      setCustomerConcentrationDraft(
                                        current => ({
                                          ...current,
                                          top5: event.target.value.replace(
                                            /,/g,
                                            ''
                                          )
                                        })
                                      );
                                    }}
                                    placeholder="Enter Value"
                                    value={customerConcentrationDraft.top5}
                                  />
                                  <InputGroupAddon align="inline-end">
                                    <InputGroupText>%</InputGroupText>
                                  </InputGroupAddon>
                                </InputGroup>
                              </Field>
                              <Field>
                                <FieldLabel htmlFor="customer-top10-field">
                                  Top 10
                                </FieldLabel>
                                <InputGroup>
                                  <NumberField
                                    id="customer-top10-field"
                                    max={100}
                                    onChange={event => {
                                      setCustomerConcentrationDraft(
                                        current => ({
                                          ...current,
                                          top10: event.target.value.replace(
                                            /,/g,
                                            ''
                                          )
                                        })
                                      );
                                    }}
                                    placeholder="Enter Value"
                                    value={customerConcentrationDraft.top10}
                                  />
                                  <InputGroupAddon align="inline-end">
                                    <InputGroupText>%</InputGroupText>
                                  </InputGroupAddon>
                                </InputGroup>
                              </Field>
                            </div>
                          </FieldGroup>
                        </DialogBody>

                        <DialogFooter>
                          <Button
                            disabled={isUpdatingOverview}
                            onClick={() =>
                              void handleUpdateCustomerConcentration()
                            }
                            type="button"
                          >
                            {isUpdatingOverview && <Spinner />}
                            Save
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  }
                  title="Customer Concentration"
                />
                <Card>
                  <div className="grid gap-5 @md:grid-cols-2">
                    <CapexValueRow
                      label="Top 1"
                      value={formatPercent(companyOverview?.concentrationTop1, {
                        isDecimal: true,
                        showNullAs: '-'
                      })}
                    />
                    <CapexValueRow
                      label="Top 3"
                      value={formatPercent(companyOverview?.concentrationTop3, {
                        isDecimal: true,
                        showNullAs: '-'
                      })}
                    />
                    <CapexValueRow
                      label="Top 5"
                      value={formatPercent(companyOverview?.concentrationTop5, {
                        isDecimal: true,
                        showNullAs: '-'
                      })}
                    />
                    <CapexValueRow
                      label="Top 10"
                      value={formatPercent(
                        companyOverview?.concentrationTop10,
                        { isDecimal: true, showNullAs: '-' }
                      )}
                    />
                  </div>
                </Card>
              </div>
              <div className="space-y-2.5">
                <SectionHeader
                  actions={
                    <Dialog
                      onOpenChange={setIsCustomerCommentaryOpen}
                      open={isCustomerCommentaryOpen}
                    >
                      <DialogTrigger asChild>
                        <EditButton />
                      </DialogTrigger>
                      <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>
                            Customer Commentary
                            <Badge variant="yellow">Unsaved</Badge>
                          </DialogTitle>
                        </DialogHeader>

                        <DialogBody>
                          <FieldGroup>
                            <Field>
                              <FieldLabel htmlFor="customer-commentary-field">
                                Customer Commentary
                              </FieldLabel>
                              <RichTextField
                                defaultValue={
                                  companyOverview?.customerCommentary ?? ''
                                }
                                id="customer-commentary-field"
                                ref={customerCommentaryFieldRef}
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="customer-contract-field">
                                Contract Commentary
                              </FieldLabel>
                              <RichTextField
                                defaultValue={
                                  companyOverview?.contractCommentary ?? ''
                                }
                                id="customer-contract-field"
                                ref={contractCommentaryFieldRef}
                              />
                            </Field>
                          </FieldGroup>
                        </DialogBody>

                        <DialogFooter>
                          <Button
                            disabled={isUpdatingOverview}
                            onClick={() =>
                              void handleUpdateCustomerCommentary()
                            }
                            type="button"
                          >
                            {isUpdatingOverview && <Spinner />}
                            Save
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  }
                  title="Commentary"
                />
                <Card>
                  <div className="space-y-5">
                    <MutedTextareaField
                      label="Customer Commentary"
                      value={companyOverview?.customerCommentary ?? ''}
                    />
                    <MutedTextareaField
                      label="Contract Commentary"
                      value={companyOverview?.contractCommentary ?? ''}
                    />
                  </div>
                </Card>
              </div>
            </TabsContent>
            <TabsContent
              className="space-y-2.5"
              value="supplier"
            >
              <div className="space-y-2.5">
                <SectionHeader
                  actions={
                    <Dialog
                      onOpenChange={setIsSupplierGeneralOpen}
                      open={isSupplierGeneralOpen}
                    >
                      <DialogTrigger asChild>
                        <EditButton />
                      </DialogTrigger>
                      <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>
                            Supplier General
                            <Badge variant="yellow">Unsaved</Badge>
                          </DialogTitle>
                        </DialogHeader>

                        <DialogBody>
                          <FieldGroup>
                            <Field>
                              <FieldLabel htmlFor="supplier-domestic-field">
                                Domestic / International
                              </FieldLabel>
                              <RichTextField
                                defaultValue={
                                  companyOverview?.domesticInternational ?? ''
                                }
                                id="supplier-domestic-field"
                                ref={supplierDomesticInternationalFieldRef}
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="supplier-redundancy-field">
                                Redundancy - Alternate Sources
                              </FieldLabel>
                              <RichTextField
                                defaultValue={
                                  companyOverview?.redundancyAlternateSources ??
                                  ''
                                }
                                id="supplier-redundancy-field"
                                ref={supplierRedundancyAlternateSourcesFieldRef}
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="supplier-commodity-field">
                                Commodity Exposure
                              </FieldLabel>
                              <RichTextField
                                defaultValue={
                                  companyOverview?.commodityExposure ?? ''
                                }
                                id="supplier-commodity-field"
                                ref={supplierCommodityExposureFieldRef}
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="supplier-pricing-field">
                                Pricing Trends
                              </FieldLabel>
                              <RichTextField
                                defaultValue={
                                  companyOverview?.pricingTrends ?? ''
                                }
                                id="supplier-pricing-field"
                                ref={supplierPricingTrendsFieldRef}
                              />
                            </Field>
                          </FieldGroup>
                        </DialogBody>

                        <DialogFooter>
                          <Button
                            disabled={isUpdatingOverview}
                            onClick={() => void handleUpdateSupplierGeneral()}
                            type="button"
                          >
                            {isUpdatingOverview && <Spinner />}
                            Save
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  }
                  title="General"
                />
                <Card>
                  <div className="space-y-5">
                    <TextAreaField
                      label="Domestic / International"
                      value={companyOverview?.domesticInternational ?? ''}
                    />
                    <TextAreaField
                      label="Redundancy - Alternate Sources"
                      value={companyOverview?.redundancyAlternateSources ?? ''}
                    />
                    <TextAreaField
                      label="Commodity Exposure"
                      value={companyOverview?.commodityExposure ?? ''}
                    />
                    <TextAreaField
                      label="Pricing Trends"
                      value={companyOverview?.pricingTrends ?? ''}
                    />
                  </div>
                </Card>
              </div>
              <div className="space-y-2.5">
                <SectionHeader
                  actions={
                    <Dialog
                      onOpenChange={setIsSupplierCommentaryOpen}
                      open={isSupplierCommentaryOpen}
                    >
                      <DialogTrigger asChild>
                        <EditButton />
                      </DialogTrigger>
                      <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>
                            Supplier Commentary
                            <Badge variant="yellow">Unsaved</Badge>
                          </DialogTitle>
                        </DialogHeader>

                        <DialogBody>
                          <FieldGroup>
                            <Field>
                              <FieldLabel htmlFor="supplier-concentration-field">
                                Concentration Commentary
                              </FieldLabel>
                              <RichTextField
                                defaultValue={
                                  companyOverview?.customerCommentary ?? ''
                                }
                                id="supplier-concentration-field"
                                ref={supplierConcentrationCommentaryFieldRef}
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="supplier-key-field">
                                Key Supplier Commentary
                              </FieldLabel>
                              <RichTextField
                                defaultValue={
                                  companyOverview?.keySupplierCommentary ?? ''
                                }
                                id="supplier-key-field"
                                ref={supplierKeyCommentaryFieldRef}
                              />
                            </Field>
                          </FieldGroup>
                        </DialogBody>

                        <DialogFooter>
                          <Button
                            disabled={isUpdatingOverview}
                            onClick={() =>
                              void handleUpdateSupplierCommentary()
                            }
                            type="button"
                          >
                            {isUpdatingOverview && <Spinner />}
                            Save
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  }
                  title="Commentary"
                />
                <Card>
                  <div className="space-y-5">
                    <MutedTextareaField
                      label="Customer Commentary"
                      value={companyOverview?.customerCommentary ?? ''}
                    />
                    <MutedTextareaField
                      label="Key Supplier Commentary"
                      value={companyOverview?.keySupplierCommentary ?? ''}
                    />
                  </div>
                </Card>
              </div>
            </TabsContent>
            <TabsContent
              className="space-y-2.5"
              value="infra"
            >
              <SectionHeader
                actions={
                  <Dialog
                    onOpenChange={setIsInfrastructureOpen}
                    open={isInfrastructureOpen}
                  >
                    <DialogTrigger asChild>
                      <EditButton />
                    </DialogTrigger>
                    <DialogContent aria-describedby={undefined}>
                      <DialogHeader>
                        <DialogTitle>
                          Operational Infrastructure & Technology
                          <Badge variant="yellow">Unsaved</Badge>
                        </DialogTitle>
                      </DialogHeader>

                      <DialogBody>
                        <FieldGroup>
                          <Field>
                            <FieldLabel htmlFor="infra-tech-field">
                              Technology Stack
                            </FieldLabel>
                            <RichTextField
                              defaultValue={
                                operationalInfrastructure?.technologyStack ?? ''
                              }
                              id="infra-tech-field"
                              ref={infrastructureTechnologyStackFieldRef}
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="infra-security-field">
                              Security
                            </FieldLabel>
                            <RichTextField
                              defaultValue={
                                operationalInfrastructure?.security ?? ''
                              }
                              id="infra-security-field"
                              ref={infrastructureSecurityFieldRef}
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="infra-scalability-field">
                              Scalability
                            </FieldLabel>
                            <RichTextField
                              defaultValue={
                                operationalInfrastructure?.scalability ?? ''
                              }
                              id="infra-scalability-field"
                              ref={infrastructureScalabilityFieldRef}
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="infra-development-field">
                              Development
                            </FieldLabel>
                            <RichTextField
                              defaultValue={
                                operationalInfrastructure?.development ?? ''
                              }
                              id="infra-development-field"
                              ref={infrastructureDevelopmentFieldRef}
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="infra-support-field">
                              Support
                            </FieldLabel>
                            <RichTextField
                              defaultValue={
                                operationalInfrastructure?.support ?? ''
                              }
                              id="infra-support-field"
                              ref={infrastructureSupportFieldRef}
                            />
                          </Field>
                        </FieldGroup>
                      </DialogBody>

                      <DialogFooter>
                        <Button
                          disabled={isUpsertingInfrastructure}
                          onClick={() => void handleSaveInfrastructure()}
                          type="button"
                        >
                          {isUpsertingInfrastructure && <Spinner />}
                          Save
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                }
                title="Operational Infrastructure & Technology"
              />
              <Card>
                <div className="space-y-5">
                  <MutedTextareaField
                    label="Technology Stack"
                    value={operationalInfrastructure?.technologyStack ?? ''}
                  />
                  <MutedTextareaField
                    label="Security"
                    value={operationalInfrastructure?.security ?? ''}
                  />
                  <MutedTextareaField
                    label="Scalability"
                    value={operationalInfrastructure?.scalability ?? ''}
                  />
                  <MutedTextareaField
                    label="Development"
                    value={operationalInfrastructure?.development ?? ''}
                  />
                  <MutedTextareaField
                    label="Support"
                    value={operationalInfrastructure?.support ?? ''}
                  />
                </div>
              </Card>
            </TabsContent>
            <TabsContent
              className="space-y-2.5"
              value="mmri"
            >
              <MmriTab targetEntityId={mmriTargetEntityId} />
            </TabsContent>
          </Tabs>
        </div>
      </RightSidebarWrapper>
    </>
  );
};

export default InvestmentsDirectIdCompany;
